import { useEffect, useRef, type ReactNode } from 'react';
import type { View as RNView, StyleProp, ViewStyle } from 'react-native';

import {
  useReplyDraftTourActions,
  useReplyDraftTourStore,
  useReplyDraftTourStoreApi,
} from './replyDraftTourContext';
import {
  REPLY_DRAFT_TOUR_SEGMENTS,
  tourZoneKey,
  type ReplyDraftTourSegment,
} from '../../model/tour';
import { selectIsTourLive } from '../../state/replyDraftTourStore';

import { SpotlightZone } from '@/components/spotlight/SpotlightTour';
import { View } from '@/components/ui/view';

const HIGHLIGHT_RADIUS = 12;

type ReplyDraftTourZoneProps = {
  segment: ReplyDraftTourSegment;
  zone: number;
  /** Off for a target that floats over the body, such as the footer, which is always in sight. */
  scrollsIntoView?: boolean;
  style?: StyleProp<ViewStyle>;
  children: ReactNode;
};

/**
 * Marks a part of the wizard as one step of the reply draft tour.
 *
 * The wrapper always renders and only its registration toggles, so a point
 * half typed in the note field keeps focus when the tour starts.
 */
export function ReplyDraftTourZone({
  segment,
  zone,
  scrollsIntoView = true,
  style,
  children,
}: ReplyDraftTourZoneProps) {
  const store = useReplyDraftTourStoreApi();

  const { setZoneNode } = useReplyDraftTourActions();

  const nodeRef = useRef<RNView>(null);

  const isLive = useReplyDraftTourStore(selectIsTourLive);

  const key = tourZoneKey(segment, zone);

  useEffect(() => {
    if (!isLive) {
      return;
    }

    if (scrollsIntoView) {
      setZoneNode(key, nodeRef.current);
    }

    store.getState().setZone(key, true);

    return () => {
      store.getState().setZone(key, false);

      setZoneNode(key, null);
    };
  }, [isLive, key, store, setZoneNode, scrollsIntoView]);

  return (
    <SpotlightZone
      tourKey={REPLY_DRAFT_TOUR_SEGMENTS[segment].tourKey}
      zone={zone}
      active={isLive}
      radius={HIGHLIGHT_RADIUS}
      style={style}>
      <View ref={nodeRef}>{children}</View>
    </SpotlightZone>
  );
}
